import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import AddressFields, { Address, isCompleteAddress } from 'components/AddressFields/AddressFields';
import { FlowComponentType } from 'routes/types';
import { getApplicationData as selectApplicationData } from 'selectors/getApplicationData';
import { getLoanOffer } from 'selectors/getLoanOfferData';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useLoanOfferProcessor from 'hooks/useLoanOfferProcessor';
import { updateExtendedIdentityCheck, updateLoanOffer } from 'thunks';
import { ApplyingResult } from 'enums/FlowNextResults';
import UserSessionWarning from 'components/Common/UserSessionWarning/UserSessionWarning';
import { ExtendedIdentityFormState } from 'components/ExtendedIdentityCheck';

import styles from './ExtendedIdentityAddresses.module.scss';

const hasStreetNumber = (street?: string): boolean => /\d/.test(street ?? '');

const ExtendedIdentityAddresses = ({ handleNext }: FlowComponentType): JSX.Element => {
  const location = useLocation();
  const identityState = (location.state as ExtendedIdentityFormState | null) ?? {};
  const { application } = useSelector(selectApplicationData);
  const { isLoading } = useSelector(getLoanOffer);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { processLoanOffer } = useLoanOfferProcessor({ handleNext });

  const [currentAddress, setCurrentAddress] = useState<Address>(() => ({
    line1: application?.borrowerStreetAddress,
    city: application?.borrowerCity,
    state: application?.borrowerStateOrProvince,
    zip: application?.borrowerZipOrPostalCode,
  }));
  const [previousAddress, setPreviousAddress] = useState<Address>({});
  const [includePrevious, setIncludePrevious] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [rerunFailed, setRerunFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentHasAnyChange = useMemo(
    () =>
      Boolean(
        currentAddress.line1 !== application?.borrowerStreetAddress ||
          currentAddress.city !== application?.borrowerCity ||
          currentAddress.state !== application?.borrowerStateOrProvince ||
          currentAddress.zip !== application?.borrowerZipOrPostalCode,
      ),
    [currentAddress, application],
  );

  const handleSubmit = async () => {
    setErrorMessage(undefined);
    setRerunFailed(false);

    if (currentHasAnyChange || isCompleteAddress(currentAddress)) {
      if (!isCompleteAddress(currentAddress) && currentHasAnyChange) {
        setErrorMessage('Please complete all current address fields');
        return;
      }
      if (isCompleteAddress(currentAddress) && !hasStreetNumber(currentAddress.line1)) {
        setErrorMessage('Current street address should include a street number');
        return;
      }
    }

    if (includePrevious) {
      if (!isCompleteAddress(previousAddress)) {
        setErrorMessage('Please complete all previous address fields, or skip previous address');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const shouldSendCurrent =
        isCompleteAddress(currentAddress) && (currentHasAnyChange || !application?.borrowerStreetAddress);

      await dispatchWithUnwrap(
        updateExtendedIdentityCheck({
          applicationId: application!.id,
          ...(identityState.middleName ? { middleName: identityState.middleName } : {}),
          ...(identityState.socialSecurityNumber ? { socialSecurityNumber: identityState.socialSecurityNumber } : {}),
          ...(shouldSendCurrent
            ? {
                currentAddress: {
                  street: currentAddress.line1!,
                  city: currentAddress.city!,
                  state: currentAddress.state!,
                  zipCode: currentAddress.zip!,
                },
              }
            : {}),
          ...(includePrevious && isCompleteAddress(previousAddress)
            ? {
                previousAddress: {
                  street: previousAddress.line1!,
                  city: previousAddress.city!,
                  state: previousAddress.state!,
                  zipCode: previousAddress.zip!,
                },
              }
            : {}),
        }),
      );

      const runLoanOffer = () => dispatchWithUnwrap(updateLoanOffer({ applicationId: application!.id }));
      const res = await processLoanOffer(runLoanOffer);

      if (!res || res.data.credit_passed) {
        handleNext(ApplyingResult.Done);
      } else {
        setRerunFailed(true);
      }
    } catch {
      setErrorMessage('Something went wrong saving your details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer
      title="Confirm your address"
      subtitle="We couldn't match your credit profile with the address on file. Update your current address or add a previous one to help us find the right file."
      className={styles.container}
    >
      <AddressFields
        prefix="Current"
        value={currentAddress}
        onChange={(address) => {
          setCurrentAddress(address);
        }}
      />

      <button type="button" className={styles.togglePrevious} onClick={() => setIncludePrevious((value) => !value)}>
        {includePrevious ? 'Remove previous address' : 'Add a previous address (optional)'}
      </button>

      {includePrevious && (
        <AddressFields
          prefix="Previous"
          value={previousAddress}
          onChange={(address) => {
            setPreviousAddress(address);
          }}
        />
      )}

      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      {rerunFailed && (
        <UserSessionWarning
          message="We still couldn't match your credit profile. Please update your details and try again."
          showContactUsOption
        />
      )}

      <Button className={styles.primaryButton} isLoading={isLoading || isSubmitting} onClick={handleSubmit}>
        Continue application
      </Button>
    </FormContainer>
  );
};

export default ExtendedIdentityAddresses;
